import { useEffect, useState } from "react";
import { db } from "../firebase/config";
import { client } from "./Client";
import describeQueryError from "../helpers/firestore/describeQueryError";
import { deserializeWidget } from "../components/liveReport/reportBuilder2/utils/serializer";

/**
 * Keep the first widget per ID, dropping duplicates. Bad data can leave two
 * Firestore docs sharing one widget ID; the editor's setWidgets already dedupes
 * by ID, but the read-only Viewer renders the raw list — so without this it
 * would render the duplicate twice (e.g. a doubled cover title).
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-livereport-viewer-duplicate-widget-id
 *
 * @param {BaseWidget[]} list
 * @returns {BaseWidget[]}
 */
const dedupeById = (list) => {
    const seen = new Set();
    return list.filter(w => {
        if (seen.has(w.id)) return false;
        seen.add(w.id);
        return true;
    });
};

/**
 * Subscribe to widgets for a report.
 *
 * Two orthogonal concerns:
 *  1. **Dual-subscribe vs single-branch.** Templates can be owned by any tenant,
 *     and their widgets may have been written before `IsTemplate` was denormalized.
 *     When the report is a template, we subscribe to both the own-CompagnyID
 *     branch *and* the `IsTemplate==true` branch and merge results — whichever
 *     branch matches, widgets load. For own non-template reports, a single
 *     `CompagnyID==client` branch is enough.
 *  2. **Clear docids vs preserve.** Only copy-of-template flows (`asCopy`) clear
 *     docids, so saves create new widgets instead of overwriting the source.
 *     In-place template editing (admin) preserves docids so saves update the
 *     existing widgets.
 *
 * @typedef {import("../components/liveReport/reportBuilder2/widgets/BaseWidget").BaseWidget} BaseWidget
 *
 * @param {object} params
 * @param {string} params.reportId - ID (not docid) of the report whose widgets to load
 * @param {import("../types/LiveReports2").LiveReports2Doc | null | undefined} [params.report] - Loaded parent report. Required when `asCopy` is false — the hook waits until the report loads before subscribing, and inspects `IsTemplate` to decide whether to dual-subscribe.
 * @param {boolean} [params.asCopy] - If true, clears docids so saves create new widgets (copy-of-template flow). Also forces dual-subscribe without waiting for `report` to load.
 * @returns {BaseWidget[]}
 */
const useLiveReportWidgets = ({ reportId, report, asCopy = false }) => {
    /** @type {ReturnType<typeof useState<BaseWidget[]>>} */
    const [widgets, setWidgets] = useState([]);

    const reportCompagnyId = report?.CompagnyID;
    const reportIsTemplate = report?.IsTemplate === true;

    useEffect(() => {
        if (!reportId) return;

        // Wait for the report to load unless the caller already knows this is a copy
        // (asCopy is set from the URL's copyOf param, so no report lookup is needed).
        if (!asCopy && !reportCompagnyId) return;

        const base = db.collection('LiveReportWidgets')
            .where('ReportId', '==', reportId)
            .orderBy('Order', 'asc');

        const shouldDualSubscribe = asCopy || reportIsTemplate;
        const clearDocIds = asCopy;

        if (shouldDualSubscribe) {
            /** @type {Map<string, any>} */
            const merged = new Map();
            let unsubOwn = null;
            let unsubTpl = null;

            const apply = () => {
                const sorted = Array.from(merged.values())
                    .sort((a, b) => (Number(a.Order) || 0) - (Number(b.Order) || 0));
                const parsed = sorted
                    .map(w => deserializeWidget(w, true))
                    .filter(w => !w.deleted);
                setWidgets(dedupeById(parsed));
            };

            unsubOwn = base
                .where('CompagnyID', '==', client)
                .onSnapshot(snap => {
                    snap.forEach(doc => merged.set(doc.id, {
                        ...doc.data(),
                        docid: clearDocIds ? null : doc.id,
                    }));
                    apply();
                    if (!snap.empty && unsubTpl) {
                        unsubTpl();
                        unsubTpl = null;
                    }
                }, error => console.error(
                    describeQueryError('LiveReportWidgets', [['CompagnyID', '==', client]], error), error
                ));

            unsubTpl = base
                .where('IsTemplate', '==', true)
                .onSnapshot(snap => {
                    snap.forEach(doc => merged.set(doc.id, {
                        ...doc.data(),
                        docid: clearDocIds ? null : doc.id,
                    }));
                    apply();
                    if (!snap.empty && unsubOwn) {
                        unsubOwn();
                        unsubOwn = null;
                    }
                }, error => console.error(
                    describeQueryError('LiveReportWidgets', [['IsTemplate', '==', true]], error), error
                ));

            return () => {
                if (unsubOwn) unsubOwn();
                if (unsubTpl) unsubTpl();
            };
        }

        // Own, non-template report: single-branch is sufficient.
        const unsub = base
            .where('CompagnyID', '==', client)
            .onSnapshot(snap => {
                /** @type {Array<any>} */
                const docs = [];
                snap.forEach(doc => docs.push({ ...doc.data(), docid: doc.id }));
                const parsed = docs
                    .map(w => deserializeWidget(w, true))
                    .filter(w => !w.deleted);
                setWidgets(dedupeById(parsed));
            }, error => console.error(
                describeQueryError('LiveReportWidgets', [['CompagnyID', '==', client]], error), error
            ));

        return () => unsub();
    }, [reportId, reportCompagnyId, reportIsTemplate, asCopy]);

    return widgets;
};

export default useLiveReportWidgets;
